import type { InboxFilter } from "@kwp/contracts";
import type { IconName } from "../../ui/icons";

/**
 * The mailboxes the sidebar lists on Mail (WP-056), in its order. Each is one query on the thread
 * cache (`inboxConstraints`); the URL names it as `box`, and no `box` is the inbox.
 */
export const BOXES = [
  { key: "inbox", label: "Inbox", icon: "inbox" },
  { key: "snoozed", label: "Snoozed", icon: "snooze" },
  { key: "sent", label: "Sent", icon: "sent" },
  { key: "archive", label: "Archived", icon: "archive" },
  { key: "spam", label: "Spam", icon: "spam" },
  { key: "trash", label: "Trash", icon: "trash" },
] as const satisfies ReadonlyArray<{ key: string; label: string; icon: IconName }>;

export type Box = (typeof BOXES)[number]["key"];

export const BOX_KEYS = BOXES.map((b) => b.key) as [Box, ...Box[]];

/** The mailbox a URL shows. Snoozed and Sent were quick filters before WP-056; old links still open them. */
export function boxOf(search: { box?: Box | undefined; filter?: InboxFilter | undefined }): Box {
  if (search.box !== undefined) return search.box;
  if (search.filter === "snoozed" || search.filter === "sent") return search.filter;
  return "inbox";
}

export function boxLabel(box: Box): string {
  return (BOXES.find((b) => b.key === box) as (typeof BOXES)[number]).label;
}

/** The action that takes a conversation back to the inbox from this mailbox, if it has one. */
export function returnAction(box: Box): { kind: "unarchive" | "notSpam" | "untrash"; label: string } | null {
  if (box === "archive") return { kind: "unarchive", label: "Move to inbox" };
  if (box === "spam") return { kind: "notSpam", label: "Not spam" };
  if (box === "trash") return { kind: "untrash", label: "Restore" };
  return null;
}
